import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { InputText } from 'primereact/inputtext'
import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'
import { InsightsDashboardPoolState } from '@aytm/app/insights/dashboard/duckness/ducks/types'
import { useDashboard, useDateTime } from '@aytm/app/insights/dashboard/components/hooks'
import { TITLE_MAX_LENGTH } from '@aytm/app/insights/dashboard/components/sidebar/ReportsPanel/ReportsContext'
import { useReports } from '@aytm/app/insights/dashboard/components/sidebar/ReportsPanel/useReports'
import { InsightsButton } from '@aytm/app/insights/dashboard/components/sidebar/ShareDialog/InsightsButton'
import { decode, encodeAll } from '@aytm/app/insights/dashboard/lib/sanitize'
import { isValidEmail } from '@aytm/app/insights/dashboard/lib/validation'
import { FiltersOrderedMap } from '@aytm/app/insights/dashboard/models/BlockModel'
import { SendStatus } from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import { FilterOptions } from '@aytm/app/insights/dashboard/lib/filters'
import { getMinSampleSizeValue, FILTERS_SAMPLE_SIZE_ID } from '@aytm/app/insights/dashboard/helpers/filters'
import Overlay from '@aytm/app/insights/dashboard/components/ui/Overlay'

import { stateSelector, shouldUpdate, type StateSelectorParams } from './selectors'

import mcss from './ShareDialog.module.scss'

import { MSEC_IN_SEC } from '@aytm/app/insights/dashboard/lib/datetime'

const titleHTMLValidation = { required: true, maxLength: TITLE_MAX_LENGTH } as const

export interface ShareDialogProps {
  show: boolean
  onHide: () => void
}

export function ShareDialog({ show, onHide }: ShareDialogProps) {
  const {
    blockModel,
    requestShareURL,
    isErrorRequestingShareURL,
    sendInvitationLink,
    shareRequested,
    shareURLs,
    editorMode,
    invitationError: error,
    invitationStatus: sendStatus
  } = useDashboard()

  const { renderDate } = useDateTime()

  const { onSave, onSaveAs, isTitleInvalidCheck, isCurrentModified, onRevertCurrent } = useReports()

  const blockTitle = useMemo<string>(() => blockModel.get('title') || '', [blockModel])

  const { filterOptions, panelModelFilters } = usePool<
    typeof InsightsDashboardPool,
    StateSelectorParams,
    InsightsDashboardPoolState
  >(InsightsDashboardPool, stateSelector, shouldUpdate)

  const shareLink = useMemo<string | null>(() => {
    if (shareURLs && shareRequested) {
      return shareURLs.find(({ uid }) => uid === shareRequested)?.url ?? null
    }
    return null
  }, [shareURLs, shareRequested])

  const [isCopied, setIsCopied] = useState(false)

  const shareURLRequested = useRef<Record<string, boolean | void>>(shareLink ? { [shareRequested]: true } : {})

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const invalidEmailRemainderRef = useRef<string | null>(null)
  const [title, setTitle] = useState(blockTitle ?? '')
  const { invalid: isTitleInvalid, validation: titleInvalidReason } = useMemo(
    () => isTitleInvalidCheck(title),
    [isTitleInvalidCheck, title]
  )

  const isEmailValid = useMemo(
    () =>
      !email ||
      email
        .split(/([,;]\s*)|(\s+)/)
        .filter(Boolean)
        .some(email => isValidEmail(email) && (email || '').length <= TITLE_MAX_LENGTH),
    [email]
  )

  const isModifiedLocally = useMemo(() => blockTitle !== (title || '').trim(), [blockTitle, title])

  const blockFiltersExplained = useMemo(
    () => verboseFilters(panelModelFilters, filterOptions),
    [panelModelFilters, filterOptions]
  )

  const handleHide = useCallback(() => {
    setEmail('')
    onHide()
  }, [onHide])

  const handleRevert = useCallback(() => {
    onRevertCurrent()
    setTitle(blockTitle)
    handleHide()
  }, [blockTitle, handleHide, onRevertCurrent])

  const handleSaveAs = useCallback(() => {
    onSaveAs(isTitleInvalid ? null : title)
    handleHide()
  }, [handleHide, isTitleInvalid, onSaveAs, title])

  const handleSave = useCallback(() => {
    if (isTitleInvalid) {
      return
    }
    onSave(title)
    handleHide()
  }, [handleHide, isTitleInvalid, title, onSave])

  const handleSend = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      evt.preventDefault()

      if (!shareLink || !email) {
        return
      }

      const { valid, invalid } = email
        .split(/[\s,;]+/)
        .filter(Boolean)
        .reduce(
          (acc, email) =>
            isValidEmail(email) && (email || '').length < 120
              ? { ...acc, valid: [...acc.valid, email] }
              : /^\s*$/.test(email)
                ? acc
                : { ...acc, invalid: [...acc.invalid, email] },
          { valid: [], invalid: [] } as { valid: string[]; invalid: string[] }
        )

      invalidEmailRemainderRef.current = invalid.filter(Boolean).join(', ')

      const shareLinkKey = shareLink.split('/').pop()

      sendInvitationLink(shareLinkKey, valid)
    },
    [email, sendInvitationLink, shareLink]
  )

  const handleCopy = useCallback(() => {
    new Promise(rs => {
      rs(
        window.navigator?.clipboard?.writeText?.(composeShareURL(shareLink)) ??
          copyToClipboard(composeShareURL(shareLink))
      )
    })
      .then(() => {
        setIsCopied(true)
        setTimeout(() => {
          setIsCopied(false)
        }, 5 * 1000)
      })
      .catch(() => {
        setIsCopied(false)
      })
  }, [shareLink])

  const initializeDialog = useCallback(() => {
    // need this to initialize on block change
    const blockTitle = blockModel.get<string>('title')
    setTitle(blockTitle ?? '')
    setEmail('')
  }, [blockModel])

  useEffect(() => {
    initializeDialog()
  }, [initializeDialog])

  useEffect(() => {
    if (sendStatus === SendStatus.Sent) {
      setEmail(invalidEmailRemainderRef.current ?? '')
      if (invalidEmailRemainderRef.current.length) {
        setEmailError('Please ensure the remaining entries are in proper email format before clicking send.')
        setTimeout(() => {
          setEmailError(null)
        }, 8 * MSEC_IN_SEC)
      }
      invalidEmailRemainderRef.current = null
    }
    if (sendStatus === SendStatus.SentWithError || sendStatus === SendStatus.Sent) {
      setTimeout(() => {
        sendInvitationLink(undefined, undefined, true)
      }, 7 * MSEC_IN_SEC)
    }
  }, [error, sendInvitationLink, sendStatus])

  useEffect(() => {
    if (!shareRequested || shareLink || (shareURLRequested.current && shareURLRequested.current[shareRequested])) {
      return
    }

    shareURLRequested.current[shareRequested] = requestShareURL(shareRequested)
  }, [requestShareURL, shareRequested, shareLink])

  return (
    <Overlay
      show={show}
      onOuterClick={handleHide}
      onExit={handleHide}
      className={classNames('PRAYTMTheme', mcss['share-dialog'])}
    >
      <div className="p-p-3 p-pb-4 flex flex-column align-items-stretch">
        <div className="flex flex-row align-content-start p-mb-2">
          <InsightsButton
            type="button"
            label={'Revert'}
            disabled={!isCurrentModified && !isModifiedLocally}
            secondary
            rounded
            className="p-mr-3 p-px-4 p-py-2"
            onClick={handleRevert}
          />
          <InsightsButton
            type="button"
            label={'Save as'}
            disabled={!editorMode}
            secondary
            rounded
            className="p-mr-3 p-px-4 p-py-2"
            onClick={handleSaveAs}
          />
          <InsightsButton
            type="button"
            icon={
              isCurrentModified || isModifiedLocally ? 'pi pi-circle-fill insights-sidebar-indicator-modified' : null
            }
            label={'Save'}
            disabled={(!isCurrentModified && !isModifiedLocally) || isTitleInvalid || !editorMode}
            rounded
            className="p-px-4 p-py-2"
            onClick={handleSave}
          />
        </div>
        <hr className="p-my-3 p-d-block" />
        <div className="uppercase p-mb-4 w-full p-text-left">{'SHARE OPTIONS'}</div>
        <div className="p-mb-4 w-full flex flex-column align-items-stretch">
          <InputText
            type="text"
            {...titleHTMLValidation}
            placeholder={'Title'}
            disabled={!editorMode}
            value={decode(title || '')}
            onChange={evt => setTitle(encodeAll(evt.target?.value))}
            className={classNames(mcss['share-dialog__input'], 'p-mr-3 flex-grow-1 rounded', {
              'p-invalid': isTitleInvalid
            })}
          />
          {isTitleInvalid && (
            <div className="p-mt-2 p-text-left p-text-sm p-error">
              {'required' === titleInvalidReason
                ? 'The title is required'
                : 'max_len' === titleInvalidReason
                  ? `The title must have not more than ${TITLE_MAX_LENGTH} characters`
                  : 'uniqueness' === titleInvalidReason
                    ? 'The title must be unique'
                    : null}
            </div>
          )}
        </div>

        <div className="p-mb-4 w-full p-text-left flex flex-column">
          <label htmlFor="" className={classNames(mcss['share-dialog__input-label'], 'p-d-block w-auto p-mb-2')}>
            <i className="icon-aytmfd-email p-mr-2"></i>
            <span>{'By emails'}</span>
          </label>
          <div className="flex flex-row align-items-stretch w-full">
            <InputText
              type="email"
              placeholder="Enter emails"
              className={classNames(mcss['share-dialog__input'], 'p-mr-3 flex-grow-1 rounded', {
                'p-invalid': (email && !isEmailValid) || sendStatus === SendStatus.SentWithError
              })}
              disabled={sendStatus === SendStatus.Sending}
              value={email || ''}
              onChange={evt => setEmail(evt.target?.value)}
            />
            <InsightsButton
              type="button"
              label={['Send', 'Sending..', 'Sent!', 'Send Error'][Number(sendStatus)]}
              disabled={
                !email ||
                !shareLink ||
                (sendStatus !== SendStatus.Send && sendStatus !== SendStatus.SentWithError) ||
                !isEmailValid
              }
              loading={sendStatus === SendStatus.Sending}
              secondary
              rounded
              className="p-mr-3 p-px-4 p-py-2 uppercase"
              onClick={handleSend}
            />
          </div>
          {error && <div className="p-mt-2 p-text-left p-text-sm p-error">{error}</div>}
          {emailError && <div className="p-mt-2 p-text-left p-text-sm p-error">{emailError}</div>}
        </div>
        <div className="p-mb-5 w-full p-text-left flex flex-column">
          <label htmlFor="" className={classNames(mcss['share-dialog__input-label'], 'p-d-block w-auto p-mb-2')}>
            <i className={classNames('icon-aytmfd-skew-link p-mr-2', mcss['tweaked-icon'])}></i>
            <span>{'By link'}</span>
          </label>
          <div className="flex flex-row p-align-center  w-full">
            <InputText
              value={shareLink ? composeShareURL(shareLink) : ''}
              disabled
              className={classNames(mcss['share-dialog__input'], 'p-mr-3 flex-grow-1 rounded')}
              placeholder={isErrorRequestingShareURL ? 'Error obtaining link' : 'Calculating..'}
            />
            <InsightsButton
              label={isCopied ? 'Copied!' : 'Copy'}
              type="button"
              disabled={isCopied || !shareLink}
              rounded
              className="p-mr-3 p-px-4 p-py-2 uppercase"
              onClick={handleCopy}
            />
          </div>
        </div>
        <div className={classNames(mcss['share-dialog__content'], 'flex flex-column justify-content-center')}>
          {blockModel.createdBy && (
            <div className="p-mb-3">
              Created by: <span className="p-bold">{blockModel.createdBy}</span>
              {blockModel.createdAt && (
                <>
                  {' • '}
                  {renderDate(blockModel.createdAt, 'p-bold')}
                </>
              )}
            </div>
          )}
          {blockModel.updatedBy && (
            <div className="p-mb-3">
              Modified by <span className="p-bold">{blockModel.updatedBy}</span>
              {blockModel.updatedAt && (
                <>
                  {' • '}
                  {renderDate(blockModel.updatedAt, 'p-bold')}
                </>
              )}
            </div>
          )}
          <div className={mcss['filters-block']}>
            Filters: <span>{blockFiltersExplained}</span>
          </div>
        </div>
      </div>
    </Overlay>
  )
}
export default ShareDialog

function composeShareURL(shareUrl: string) {
  return `${window.document.location.origin}${shareUrl}`
}

function verboseFilters(filters: FiltersOrderedMap, filterOptions: FilterOptions): string | React.ReactNode {
  const sample = getMinSampleSizeValue(filters)
  const sources = Array.from(Object.entries(filterOptions || {})).filter(v => (sample > 0 ? true : v[0] !== 'sample'))
  const lookup: Record<string, string> = sources.reduce(
    (acc0, [_, source]) => source.reduce((acc, item) => Object.assign(acc, { [item.id]: item.title }), acc0),
    {}
  )
  return filters?.size
    ? filters
        .reduce((acc, cur) => {
          const uid = cur.get('uid')
          if (uid !== FILTERS_SAMPLE_SIZE_ID || sample > 0) {
            acc.push(lookup[uid] ?? uid)
          }
          return acc
        }, [] as string[])
        .join(', ')
        .toLowerCase()
        .replace(/^./, char => char.toUpperCase())
    : 'none'
}

function copyToClipboard(text: string) {
  // Create a new textarea element
  const textarea = document.createElement('textarea')
  // Set the value of the textarea to the text you want to copy
  textarea.value = text
  // Make the textarea invisible
  textarea.style.position = 'fixed'
  textarea.style.left = '-999999px'
  // Append the textarea to the document
  document.body.appendChild(textarea)
  // Select and copy the text inside the textarea
  textarea.select()
  document.execCommand('copy')
  // Remove the textarea from the document
  document.body.removeChild(textarea)
}
